import { isCancel } from 'axios';
import * as actionTypes from '../../constants/actions';
import { newError } from '../errors';
import addParticipantToSpace from '../../api/spaces/addParticipantToSpace';
import {
  getParticipants,
  getParticipantsHistory,
} from '../../api/spaces/participants';
import removeParticipantFromSpace from '../../api/spaces/removeParticipantFromSpace';
import { usersSearch } from '../../api/search';
import { currentClassRoomId, classRoomName } from '../../selectors/classroom';
import showToast from '../../toasts/operations/showToast.operation';
import { formatHistoryEvents } from '../../../app/components/presentational/ClassRoom/Settings/ParticipantsTab/ParticipantHistoryDialog/historyEventsHelpers';
import { EDUCATOR_ROLE } from '../../constants/space';

// Fetch classroom participants
const fetchClassRoomParticipantsStart = (spaceId, reset, dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_PARTICIPANTS,
    status: 'started',
    spaceId,
    reset,
  });
};

const fetchClassRoomParticipantsSuccess = (participants, spaceId, dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_PARTICIPANTS,
    status: 'success',
    participants,
    spaceId,
  });
};

const fetchClassRoomParticipantsError = (error, dispatch) => {
  dispatch(newError(error, 'fetchClassRoomParticipants'));
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_PARTICIPANTS,
    status: 'error',
    error,
  });
};

export const fetchClassRoomParticipants =
  ({ skip, reset = false, sort }) =>
  (dispatch, getState) => {
    const state = getState();
    const spaceId = state.classRoom.current;

    fetchClassRoomParticipantsStart(spaceId, reset, dispatch);

    return getParticipants({ spaceId, skip, sort }).then(
      // Dispatch successfull fetch
      (participants) =>
        fetchClassRoomParticipantsSuccess(participants, spaceId, dispatch),
      // Dispatch unsuccessfull fetch
      (error) => fetchClassRoomParticipantsError(error, dispatch)
    );
  };

const addUsersAsEducatorToSpaceDispatcher = (dispatch, userIds) => {
  const getActionPayload = (status, classRoomId = null) => ({
    type: actionTypes.CLASSROOM_ADD_PARTICIPANTS,
    userIds,
    status,
    ...(classRoomId ? { classRoomId } : {}),
  });

  return {
    dispatchStart: () => dispatch(getActionPayload('started')),
    dispatchSuccess: (classRoomId) =>
      dispatch(getActionPayload('success', classRoomId)),
    dispatchError: () => dispatch(getActionPayload('error')),
  };
};

export const addUsersAsEducatorToSpace =
  (userIds) => async (dispatch, getState) => {
    const state = getState();
    const spaceId = currentClassRoomId(state);

    const addUsersAsEducatorDispatcher = addUsersAsEducatorToSpaceDispatcher(
      dispatch,
      userIds
    );
    addUsersAsEducatorDispatcher.dispatchStart();
    try {
      const addUserPromise = (userId) =>
        addParticipantToSpace({ spaceId, role: EDUCATOR_ROLE, userId });
      await Promise.all(userIds.map(addUserPromise));
      const skip = 0;
      const reset = true;
      dispatch(fetchClassRoomParticipants({ skip, reset }));

      const message =
        userIds.length === 1
          ? 'The user was successfully added as an educator.'
          : 'The users were successfully added as educators.';
      dispatch(showToast({ emoji: '✅', message }));
      addUsersAsEducatorDispatcher.dispatchSuccess(spaceId);
    } catch (e) {
      const userWord = userIds.length === 1 ? 'the user' : 'the users';
      let message = `There was an error when adding ${userWord} to the space.`;
      if (e.response.status === 404) {
        message = "The user you're trying to add has been deleted.";
      }
      if (e.response.status >= 500) {
        message =
          'Our server is temporarily unavailable, please try again in a few minutes.';
      }
      dispatch(showToast({ emoji: '😢', message }));
      addUsersAsEducatorDispatcher.dispatchError();
    }
  };

const fetchClassRoomParticipantsHistoryStart = (spaceId, reset, dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_PARTICIPANTS_HISTORY,
    status: 'started',
    spaceId,
    reset,
  });
};

const fetchClassRoomParticipantsHistorySuccess = (
  participantsHistory,
  participantsHistoryCount,
  lastEventDate,
  spaceId,
  dispatch
) => {
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_PARTICIPANTS_HISTORY,
    status: 'success',
    participantsHistory,
    participantsHistoryCount,
    lastEventDate,
    spaceId,
  });
};

const fetchClassRoomParticipantsHistoryError = (error, dispatch) => {
  dispatch(newError(error, 'fetchClassRoomParticipantsHistory'));
  dispatch({
    type: actionTypes.CLASSROOM_FETCH_PARTICIPANTS_HISTORY,
    status: 'error',
    error,
  });
};

export const getParticipantsHistoryCount = (data) =>
  Object.keys(data).reduce((totalCount, eventDate) => {
    let count = totalCount;
    count += Object.keys(data[eventDate]).length;
    return count;
  }, 0);

export const fetchClassRoomParticipantsHistory =
  ({ limit = 50, reset = false, before = new Date().toISOString() } = {}) =>
  (dispatch, getState) => {
    const state = getState();
    const spaceId = state.classRoom.current;

    fetchClassRoomParticipantsHistoryStart(spaceId, reset, dispatch);

    try {
      let allParticipantsHistory = [];
      const fetchParticipantsHistoryData = async (
        dataSpaceId,
        dataLimit,
        dataBefore
      ) => {
        const participantsHistory = await getParticipantsHistory(
          dataSpaceId,
          dataBefore,
          dataLimit
        );
        allParticipantsHistory = [
          ...allParticipantsHistory,
          ...participantsHistory,
        ];
        const formattedHistoryEvents = formatHistoryEvents(
          allParticipantsHistory
        );

        const historyEventDataCount = getParticipantsHistoryCount(
          formattedHistoryEvents
        );
        const lastEventDate =
          allParticipantsHistory.length > 0
            ? allParticipantsHistory[allParticipantsHistory.length - 1]
                .createdAt
            : before;

        return fetchClassRoomParticipantsHistorySuccess(
          formattedHistoryEvents,
          historyEventDataCount,
          lastEventDate,
          spaceId,
          dispatch
        );
      };
      return fetchParticipantsHistoryData(spaceId, limit, before);
    } catch (error) {
      return fetchClassRoomParticipantsHistoryError(error, dispatch);
    }
  };

const removeUserFromSpaceDispatcher = (dispatch, userId, spaceId) => {
  const getActionPayload = (status) => ({
    type: actionTypes.CLASSROOM_REMOVE_PARTICIPANT,
    removedClassRoomUserId: userId,
    classRoomId: spaceId,
    status,
  });

  return {
    dispatchStart: () => dispatch(getActionPayload('started')),
    dispatchSuccess: () => dispatch(getActionPayload('success')),
    dispatchError: () => dispatch(getActionPayload('error')),
  };
};

export const removeUserFromSpace =
  (userId, isCurrentUser, generateOnLeaveSuccessMessage, onLeaveSuccess) =>
  async (dispatch, getState) => {
    const state = getState();
    const spaceId = currentClassRoomId(state);
    const spaceName = classRoomName(state);

    const removeUserDispatcher = removeUserFromSpaceDispatcher(
      dispatch,
      userId,
      spaceId
    );
    removeUserDispatcher.dispatchStart();
    try {
      await removeParticipantFromSpace({ spaceId, userId });

      // Upon successful removal of the Participant from the Space,
      // display a toast to the inform the current user that the participant was successfully removed;
      // and in the specific case that the current user removed themselves, also invoke the onLeaveSuccess callback.
      if (isCurrentUser === true && onLeaveSuccess) {
        dispatch(
          showToast({
            message: generateOnLeaveSuccessMessage(spaceName),
          })
        );
        onLeaveSuccess(spaceName);
      } else {
        dispatch(
          showToast({
            emoji: '✅',
            message: 'The user was successfully removed.',
          })
        );
      }

      removeUserDispatcher.dispatchSuccess();
    } catch (e) {
      let message = 'There was an error when removing the user from the space.';
      if (e.response?.status === 404) {
        message = "The user you're trying to remove has been deleted.";
      }
      if (e.response?.status >= 500) {
        message =
          'Our server is temporarily unavailable, please try again in a few minutes.';
      }
      dispatch(showToast({ emoji: '😢', message }));
      removeUserDispatcher.dispatchError();
    }
  };

const searchClassRoomParticipantsStart = (query, dispatch) => {
  dispatch({
    type: actionTypes.CLASSROOM_SEARCH_PARTICIPANTS,
    query,
  });
};

export const searchClassRoomParticipants =
  (query) => async (dispatch, getState) => {
    searchClassRoomParticipantsStart(query, dispatch);

    if (!query) {
      const skip = 0;
      const reset = true;
      dispatch(fetchClassRoomParticipants({ skip, reset }));
      return;
    }

    const state = getState();
    const spaceId = state.classRoom.current;

    fetchClassRoomParticipantsStart(spaceId, true, dispatch);

    try {
      const users = await usersSearch({
        query,
        spaceId,
      });
      fetchClassRoomParticipantsSuccess(users, spaceId, dispatch);
    } catch (error) {
      if (isCancel(error)) {
        return;
      }

      fetchClassRoomParticipantsError(error, dispatch);
    }
  };

export const setParticipantHistoryOpen = (isOpen) => ({
  type: actionTypes.CLASSROOM_SET_PARTICIPANT_HISTORY_OPEN,
  isOpen,
});
